import { useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import FormFieldset from '@/components/atoms/Fieldset/templates/FormFieldset';
import { ReadOnlyField } from '@/components/atoms/ReadOnlyField/ReadOnlyField';
import TitleSubtitle from '@/components/atoms/TitleSubtitle/TitleSubtitle';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useIntegrations } from '@/hooks/useIntegrations';
import { cn } from '@/lib/utils';
import { SmartleadIntegration } from '@/shared/types/integration';
import { CHANNELS_URL, getDocsUrl } from '@/util/constants';

interface Props {
  integration: SmartleadIntegration;
}

export const SmartleadFieldset = ({ integration }: Props) => {
  const { t } = useTranslation();
  const { updateIntegration, updateStatus } = useIntegrations<SmartleadIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );
  const isIntegrationSetup = useMemo(
    () => !integration?.integration_id || !integration?.config?.apikey,
    [integration]
  );
  const webhookUrl = useMemo(() => {
    if (isIntegrationSetup) {
      return '';
    }
    return `${CHANNELS_URL}/v1/smartlead/${integration?.integration_id}`;
  }, [integration, isIntegrationSetup]);

  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
  } = useForm({
    mode: 'onChange',
    values: { apikey: integration?.config?.apikey ?? '' },
    resolver: yupResolver(
      yup.object().shape({
        apikey: yup
          .string()
          .trim()
          .min(32, () => t('validation.at_least', { 0: 32 }))
          .max(64, () => t('validation.less_than', { 0: 64 })),
      })
    ),
  });

  const onSubmit = (form: Record<string, string>) => {
    updateIntegration({ ...integration, config: { ...integration.config, apikey: form.apikey } });
  };

  return (
    <FormFieldset
      title={t('integrations.infobip_sms_info.overview')}
      subtitle={t('integrations.infobip_sms_info.subtitle')}
      primaryButton={{
        children: t('common.save'),
        isLoading: updateStatus === 'pending',
        disabled: !isDirty || Boolean(errors.apikey),
      }}
      onSubmit={handleSubmit(onSubmit)}
      isLoading={updateStatus === 'pending'}
    >
      <div className="mb-6">
        <Label htmlFor="apikey" className={cn('mb-2', !!errors.apikey && 'text-warning')}>
          {t('integrations.infobip_sms_info.apikey')}
        </Label>
        <Controller
          control={control}
          name="apikey"
          render={({ field }) => (
            <Input
              id="apikey"
              aria-invalid={!!errors.apikey}
              {...field}
              value={field.value ?? ''}
            />
          )}
        />
        {errors.apikey?.message && (
          <p className="mt-1 text-sm text-warning">{errors.apikey.message}</p>
        )}
      </div>
      <TitleSubtitle
        title={t('integrations.smartlead.webhook_url')}
        subtitle={t('integrations.smartlead.url_subtitle')}
        externalLink={{
          label: t('common.learn_more'),
          url: getDocsUrl('/docs/integrations/smartlead'),
        }}
      />
      {!isIntegrationSetup && (
        <div className="flex items-center gap-2">
          <ReadOnlyField className="flex-1" value={webhookUrl} />
          <CopyButton data={webhookUrl} />
        </div>
      )}
    </FormFieldset>
  );
};

export default SmartleadFieldset;
